import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  CheckCircle2,
  CalendarClock,
  ClipboardList,
  FileText,
  LineChart,
  Download,
  MessageSquare,
  LogIn,
} from "lucide-react";
import { CALENDLY_URL } from "../lib/site";
import { RUBRIC } from "../lib/operatorWalk";
import Seo from "../components/Seo";
import { businessSchema } from "../lib/seoSchemas";
import Tm from "../components/ow/Tm";
import { usePricing } from "../lib/pricing";
import { useContent } from "../lib/content";

const included = [
  { icon: CalendarClock, text: "One on-site visit per month conducted personally by Caleb or Jake" },
  { icon: ClipboardList, text: "A 20-point inspection across Product, Service, Image and Venue, Pricing and Value, and Operational Health" },
  { icon: FileText, text: "A scored monthly report published to your private client dashboard within 72 hours of the visit" },
  { icon: CheckCircle2, text: "The top three opportunities identified each month with specific, actionable recommendations" },
  { icon: LineChart, text: "Trend tracking over time on every inspection category" },
  { icon: Download, text: "PDF download of every monthly report" },
  { icon: MessageSquare, text: "A quarterly trend review conversation included at no additional charge" },
];

const steps = [
  {
    n: "01",
    title: "We schedule your monthly visit",
    body: "You choose the window: lunch service, dinner service, the weekend rush, whatever moment matters most to your business.",
  },
  {
    n: "02",
    title: "I visit as a regular guest",
    body: "I walk the restaurant the way a guest experiences it and conduct the 20-point inspection in real time.",
  },
  {
    n: "03",
    title: "Your report is published within 72 hours",
    body: "Scores, observations, your top three opportunities, and specific recommendations land in your private dashboard.",
  },
  {
    n: "04",
    title: "We review trends together quarterly",
    body: "Every quarter we talk through the trend lines and the operational levers driving your performance.",
  },
];

const staticPricing = [
  { label: "Every plan includes", value: "One monthly visit per location, the full report, dashboard access, and quarterly trend reviews" },
  { label: "Commitment", value: "No long-term commitment, monthly billing, cancel anytime" },
];

const whoFor = [
  "Multi-unit restaurant operators",
  "Franchise operators",
  "Founder-led restaurant groups",
  "Hospitality and food service businesses",
  "PE-backed portfolio companies",
];

export default function OperatorWalk() {
  const { getDisplay } = usePricing();
  const { t } = useContent();
  const owBase = getDisplay("operator_walk_base");
  const owAdditional = getDisplay("operator_walk_additional_location");
  const pricing = [
    ...(owBase ? [{ label: "Up to three locations", value: `${owBase}, dashboard access included` }] : []),
    ...(owAdditional ? [{ label: "Each additional location beyond three", value: owAdditional }] : []),
    ...staticPricing,
  ];

  return (
    <div data-testid="page-operator-walk">
      <Seo
        title={t("ow.seo.title")}
        description={t("ow.seo.description")}
        schemas={[businessSchema]}
      />

      {/* HERO */}
      <section className="bg-white py-20 lg:py-28 border-b border-ink-line" data-testid="ow-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="eyebrow mb-6">{t("ow.hero.eyebrow")}</div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-brand-700 leading-[1.02] tracking-tight max-w-4xl">
            The Operator Walk<Tm />
          </h1>
          <p className="mt-8 text-lg sm:text-xl text-ink-soft max-w-2xl leading-relaxed">
            {t("ow.hero.sub")}
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-brand-700 hover:bg-brand-800 text-white px-7 py-4 text-sm font-medium tracking-wide transition-colors group"
              data-testid="ow-hero-book-call-btn"
            >
              Book a 30-minute conversation
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <Link
              to="/portal/login"
              className="inline-flex items-center gap-2 text-brand-700 text-sm font-medium tracking-wide border-b border-brand-700 pb-1 hover:border-brand-400 transition-colors"
              data-testid="ow-hero-client-login-link"
            >
              <LogIn className="w-4 h-4" /> Client login
            </Link>
          </div>
        </div>
      </section>

      {/* VALUE PROPOSITION */}
      <section className="bg-white py-24 lg:py-28" data-testid="ow-value">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="eyebrow mb-4">Why it works</div>
              <h2 className="font-serif text-4xl sm:text-5xl text-brand-700 leading-tight">
                {t("ow.why.heading")}
              </h2>
            </div>
            <div className="lg:col-span-8 lg:pl-8 lg:border-l lg:border-ink-line">
              <p className="text-lg text-ink-soft leading-relaxed">
                {t("ow.why.p1")}
              </p>
              <p className="mt-6 text-lg text-ink-soft leading-relaxed">
                {t("ow.why.p2")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PRIVATE DASHBOARD PREVIEW */}
      <section className="bg-brand-700 py-24 lg:py-28" data-testid="ow-dashboard-preview">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-12">
            <div className="text-[11px] tracking-[0.25em] uppercase text-brand-300 mb-4">
              The Deliverable
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl text-white leading-tight">
              {t("ow.dashboard.heading")}
            </h2>
            <p className="mt-6 text-lg text-brand-100 leading-relaxed">
              {t("ow.dashboard.body")}
            </p>
          </div>
          <figure className="relative">
            <div className="overflow-hidden border border-brand-400/40 shadow-2xl bg-white">
              <img
                src="/images/operator-walk-dashboard.png"
                alt="The Operator Walk client dashboard showing a monthly operational score, a score by category breakdown, and the top opportunities for a restaurant location."
                className="w-full h-auto block"
                width={1440}
                height={1000}
                loading="lazy"
                data-testid="ow-dashboard-image"
              />
            </div>
            <figcaption className="mt-4 text-sm text-brand-200">
              Example client dashboard. Data shown is illustrative.
            </figcaption>
          </figure>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="bg-brand-100 py-24 lg:py-28" data-testid="ow-included">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-14">
            <div className="eyebrow mb-4">What's Included</div>
            <h2 className="font-serif text-4xl sm:text-5xl text-brand-700 leading-tight">
              Everything in one monthly engagement.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-brand-200 border border-brand-200">
            {included.map((item, i) => {
              const Icon = item.icon;
              return (
                <div
                  key={i}
                  className="bg-brand-100 p-8 lg:p-9 flex items-start gap-4"
                  data-testid={`ow-included-${i}`}
                >
                  <Icon className="w-6 h-6 text-brand-700 shrink-0 mt-0.5" />
                  <p className="text-base lg:text-lg text-ink-soft leading-relaxed">{item.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* THE 20-POINT INSPECTION */}
      <section className="bg-white py-24 lg:py-28" data-testid="ow-rubric">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-14">
            <div className="eyebrow mb-4">The Inspection</div>
            <h2 className="font-serif text-4xl sm:text-5xl text-brand-700 leading-tight">
              {t("ow.inspection.heading")}
            </h2>
            <p className="mt-6 text-ink-soft leading-relaxed">
              {t("ow.inspection.intro")}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-ink-line border border-ink-line">
            {RUBRIC.map((cat, i) => (
              <div key={cat.key} className="bg-white p-8 lg:p-9 flex flex-col h-full" data-testid={`ow-category-${cat.key}`}>
                <div className="text-[10px] tracking-[0.25em] uppercase text-brand-400 mb-3">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="font-serif text-2xl text-brand-700 mb-5 leading-tight">{cat.title}</h3>
                <ul className="space-y-3">
                  {cat.points.map((p) => (
                    <li key={p.id} className="flex items-start gap-3 text-sm text-ink-soft leading-relaxed">
                      <span className="text-brand-400 font-medium shrink-0">{p.id}.</span>
                      {p.label}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-brand-100 py-24 lg:py-28" data-testid="ow-how">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-14">
            <div className="eyebrow mb-4">How It Works</div>
            <h2 className="font-serif text-4xl sm:text-5xl text-brand-700 leading-tight">
              {t("ow.how.heading")}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-brand-200 border border-brand-200">
            {steps.map((s) => (
              <div key={s.n} className="bg-brand-100 p-8 lg:p-10" data-testid={`ow-step-${s.n}`}>
                <div className="font-serif text-4xl text-brand-300 mb-4">{s.n}</div>
                <h3 className="font-serif text-2xl text-brand-700 mb-3 leading-tight">{s.title}</h3>
                <p className="text-ink-soft leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="bg-white py-24 lg:py-28" data-testid="ow-pricing">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="eyebrow mb-4">Pricing</div>
              <h2 className="font-serif text-4xl sm:text-5xl text-brand-700 leading-tight">
                Simple, transparent, monthly.
              </h2>
            </div>
            <div className="lg:col-span-8 lg:pl-8 lg:border-l lg:border-ink-line">
              <div className="border border-brand-200 bg-white">
                {pricing.map((p, i) => (
                  <div
                    key={p.label}
                    className={`flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-6 py-6 ${
                      i < pricing.length - 1 ? "border-b border-brand-200" : ""
                    }`}
                    data-testid={`ow-price-${i}`}
                  >
                    <div className="font-serif text-xl text-brand-700">{p.label}</div>
                    <div className="text-ink-soft text-sm sm:text-base sm:text-right sm:max-w-xs">{p.value}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHO THIS IS FOR */}
      <section className="bg-brand-100 py-24 lg:py-28" data-testid="ow-who">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="eyebrow mb-4">Who This Is For</div>
              <h2 className="font-serif text-4xl sm:text-5xl text-brand-700 leading-tight">
                {t("ow.who.heading")}
              </h2>
              <p className="mt-6 text-ink-soft leading-relaxed">
                {t("ow.who.body")}
              </p>
            </div>
            <div className="lg:col-span-7 lg:pl-8 lg:border-l lg:border-brand-200">
              <ul className="space-y-5">
                {whoFor.map((w, i) => (
                  <li key={w} className="flex items-start gap-4 group" data-testid={`ow-who-${i}`}>
                    <CheckCircle2 className="w-5 h-5 text-brand-700 mt-0.5 shrink-0" />
                    <span className="text-lg text-ink-soft group-hover:text-brand-700 transition-colors">{w}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand-700 text-white py-20" data-testid="ow-cta">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <h2 className="font-serif text-3xl sm:text-4xl leading-tight">
              {t("ow.cta.heading")}
            </h2>
            <p className="mt-4 text-brand-100/90 max-w-xl">
              {t("ow.cta.body")}
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-brand-700 hover:bg-brand-100 transition-colors px-7 py-4 text-sm font-medium"
              data-testid="ow-cta-button"
            >
              Book a 30-minute conversation <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
